import type { Location } from 'history'
import { matchPath } from 'react-router'

import { CustomRouterStore } from './CustomRouterStore'
import type { RouteName } from './index'

interface LocationInfo<T = unknown> extends Location<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchData: Record<any, any>
  fullPath: string
}
// TODO: 타입 어떻게 해야 잘 추출할까..
type Process<T = unknown> = (location: LocationInfo<T>) => void
type PathnameKey = (typeof RouteName)[keyof typeof RouteName]
type UnregisterCallback = () => void
type Option = {
  fireImmediately: boolean
}

type TupleKey = [mathUrlKey: PathnameKey, name: string]

/***
 * URL 변경을 감지하여 외부에서 등록된 프로세스를 태우는 서비스
 * - example
 * // url 바뀔때마다 url이 매칭 성공하면 함수 실행하는 이벤트 등록
 * commonRepo.router.listener.add([url, 'eventName'], (locationInfo: LocationInfo) => { ... })
 *
 * // url에 해당하는 이벤트 모두 삭제
 * commonRepo.router.listener.delete(url)
 *
 * // 해당 url에 특정 name에 해당하는 이벤트만 삭제
 * commonRepo.router.listener.delete([url, 'myName'])
 *
 * // 등록한 이벤트 트리거
 * commonRepo.router.listener.trigger([url, 'myName'])
 ***/
export class UrlListener {
  #map = new Map<PathnameKey, Map<string, Process>>()
  #listener?: UnregisterCallback
  #lastLocation: Location<unknown>

  constructor(private router: CustomRouterStore) {
    this.#lastLocation = router.location
  }

  private static executeProcess(locationInfo: LocationInfo, process: Process) {
    process(locationInfo)
  }

  add = <T = unknown>([mathUrlKey, name]: TupleKey, process: Process<T>, opt?: Option) => {
    const urlMap = this.#map.get(mathUrlKey)
    if (!urlMap) {
      this.#map.set(mathUrlKey, new Map())
    }
    this.#map.get(mathUrlKey)?.set(name, process as Process)

    if (opt?.fireImmediately)
      UrlListener.executeProcess(this.router.getLocationInfo(), process as Process)
  }

  delete(key: PathnameKey): void

  delete(key: TupleKey): void

  delete(key: PathnameKey | TupleKey) {
    if (typeof key === 'string') {
      this.#map.delete(key)
    } else {
      this.#map.get(key[0])?.delete(key[1])
    }
  }

  // pathname, queryParams(search), hash 셋 중 하나라도 달라졌을때 실행됨
  triggerAllProcess(location: Location<unknown>) {
    if (this.#lastLocation && CustomRouterStore.isSameLocationUrl(this.#lastLocation, location)) {
      return
    }

    this.#lastLocation = this.router.location

    const locationInfo = this.router.getLocationInfo()

    // URL Map
    for (const [key, processMap] of this.#map) {
      const match = matchPath(locationInfo.pathname, { path: key })

      // Process Map
      if (match) {
        for (const [, process] of processMap) {
          UrlListener.executeProcess(locationInfo, process)
        }
      }
    }
  }
  trigger([mathUrlKey, name]: TupleKey) {
    const process = this.#map.get(mathUrlKey)?.get(name)
    if (process) {
      UrlListener.executeProcess(this.router.getLocationInfo(), process)
    }
  }

  listen = () => {
    this.router.history?.listen(() => {
      this.triggerAllProcess(this.router.location)
    })
  }
}
